/**
 * Joins two zone-carrying times into one range, keeping the zone abbreviation
 * only once when both ends share it.
 *
 * '1:30 AM PDT' + '3:30 AM PDT' reads as '1:30 AM – 3:30 AM PDT'. Repeating
 * the abbreviation on both ends says nothing the trailing one doesn't, and on
 * a narrow screen it is what pushes the headline onto a second line.
 *
 * When the two ends genuinely differ — a range that crosses a DST boundary
 * produces 'PST' and 'PDT' — both are kept, because dropping either would
 * misattribute one end to the other's clock.
 */

/**
 * Trailing zone token of a formatted time, e.g. 'PDT' in '1:30 AM PDT'.
 *
 * A bare '1:30 AM' has no zone: AM/PM is the meridiem, and treating it as one
 * would strip it and render '1:30 – 3:30 AM'.
 */
const zoneSuffix = (time: string): string => {
  const parts = time.trim().split(/\s+/)
  if (parts.length < 3) {
    return ''
  }
  const last = parts[parts.length - 1]
  return /^(AM|PM)$/i.test(last) ? '' : last
}

const withoutZone = (time: string, zone: string): string =>
  zone && time.endsWith(zone) ? time.slice(0, -zone.length).trim() : time

export const formatTimeRange = (start: string, end?: string | null): string => {
  if (!start) {
    return ''
  }
  if (!end) {
    return start
  }

  const startZone = zoneSuffix(start)
  const endZone = zoneSuffix(end)

  // Differing zones (or no zone at all) — keep both ends verbatim.
  if (!startZone || startZone !== endZone) {
    return `${start} – ${end}`
  }

  return `${withoutZone(start, startZone)} – ${end}`
}
